import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer, PageHeader } from '../../components/AssetBreadCrumb';
import { PageBackButton } from '../../components/PageChrome';
import { Skeleton } from '../../components/Skeleton';
import { getUrl } from 'aws-amplify/storage';

const UserGuidePage: React.FC = () => {
  const navigate = useNavigate();

  // State for PDF viewer
  const [loading, setLoading] = useState<boolean>(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch the PDF from S3
  useEffect(() => {
    const fetchPdfFromS3 = async () => {
      try {
        setLoading(true);
        // Get the signed URL for the file from S3
        const result = await getUrl({
          path: 'public/Documentation/UserManual.pdf',
          options: {
            validateObjectExistence: true,
            expiresIn: 3600, // URL expires in 1 hour
          }
        });

        // Set the URL to the fetched PDF
        setPdfUrl(result.url.toString());
      } catch (err) {
        console.error('Error fetching PDF from S3:', err);
        setError('Failed to load the user manual. Please try again later.');
        setLoading(false);
      }
    };

    fetchPdfFromS3();
  }, []);

  // Function to handle PDF load success
  const onDocumentLoadSuccess = () => {
    setLoading(false);
  };

  // Both failure paths read the same to the user, so they share one block
  const errorMessage =
    error ||
    (!loading && !pdfUrl
      ? 'The user manual PDF could not be loaded from S3. It may not exist at the specified location.'
      : null);

  return (
    // The PDF fills the page under a pinned header rather than scrolling the shell
    <PageContainer fullHeight>
      <PageHeader
        eyebrow="Resources"
        title="User Guide"
        description="Learn how to use the Erode platform with step-by-step instructions."
        className="shrink-0"
        action={
          <PageBackButton label="Back to Resources" onClick={() => navigate('/home/resources')} />
        }
      />

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-eai-card border border-eai-border bg-eai-surface-sunken">
        {errorMessage ? (
          <div
            role="alert"
            className="m-5 rounded-md border border-eai-danger-border bg-eai-danger-surface px-4 py-3 font-inter text-eai-body-sm text-eai-danger-text"
          >
            <p className="font-outfit font-semibold">Error</p>
            <p className="mt-1">{errorMessage}</p>
          </div>
        ) : (
          <>
            {/* Page-shaped placeholder over the frame until the iframe reports it has painted */}
            {loading && (
              <div className="absolute inset-0 z-10 flex justify-center bg-eai-surface-sunken p-6">
                <Skeleton className="h-full w-full max-w-3xl" radius="md" />
              </div>
            )}
            {pdfUrl && (
              <iframe
                src={`${pdfUrl}#pagemode=none&navpanes=0`}
                title="User Manual"
                className="h-full w-full border-none"
                onLoad={onDocumentLoadSuccess}
              />
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default UserGuidePage;
